// InventIQ Plan Configuration
// Paystack subscription tiers with feature limits

export type PlanId = 'launch_pass' | 'spark' | 'momentum' | 'precision' | 'whitelisted';

export interface PlanLimits {
  products: number;       // -1 = unlimited
  rawMaterials: number;   // -1 = unlimited
  users: number;          // -1 = unlimited
  hasReporting: boolean;
  hasAdvancedAnalytics: boolean;
  hasPurchaseHistory: boolean;
  hasXlsxExport: boolean;
  hasXlsxImport: boolean;
  hasLowStockAlerts: boolean;
}

export interface Plan {
  id: PlanId;
  name: string;
  tagline: string;
  priceMin: number;
  priceMax: number;
  priceSuffix: string;
  paystackPlanCode: string; // set via env or Paystack dashboard
  limits: PlanLimits;
  features: string[];
  highlighted?: boolean;
}

export const PLANS: Record<PlanId, Plan> = {
  launch_pass: {
    id: 'launch_pass',
    name: 'InventIQ Launch Pass',
    tagline: 'Try everything free for 1 week',
    priceMin: 0,
    priceMax: 0,
    priceSuffix: '1-week trial',
    paystackPlanCode: '',
    limits: {
      products: -1,
      rawMaterials: -1,
      users: -1,
      hasReporting: true,
      hasAdvancedAnalytics: true,
      hasPurchaseHistory: true,
      hasXlsxExport: true,
      hasXlsxImport: true,
      hasLowStockAlerts: true,
    },
    features: [
      'Full access to all features',
      'Unlimited products & recipes',
      'Unlimited raw materials',
      'All analytics & reports',
      'XLSX import & export',
      '1-week free trial',
    ],
  },
  spark: {
    id: 'spark',
    name: 'InventIQ Spark',
    tagline: 'For hobbyists and micro-businesses',
    priceMin: 9,
    priceMax: 19,
    priceSuffix: '/month',
    paystackPlanCode: process.env.NEXT_PUBLIC_PAYSTACK_PLAN_SPARK ?? '',
    limits: {
      products: 10,
      rawMaterials: 25,
      users: 1,
      hasReporting: false,
      hasAdvancedAnalytics: false,
      hasPurchaseHistory: false,
      hasXlsxExport: false,
      hasXlsxImport: false,
      hasLowStockAlerts: false,
    },
    features: [
      'Up to 10 products/recipes',
      'Up to 25 raw materials',
      '1 user',
      'Cost auto-recalculation',
      'Basic inventory tracking',
    ],
  },
  momentum: {
    id: 'momentum',
    name: 'InventIQ Momentum',
    tagline: 'For small businesses with multiple product lines',
    priceMin: 29,
    priceMax: 49,
    priceSuffix: '/month',
    paystackPlanCode: process.env.NEXT_PUBLIC_PAYSTACK_PLAN_MOMENTUM ?? '',
    highlighted: true,
    limits: {
      products: 25,
      rawMaterials: 100,
      users: 3,
      hasReporting: true,
      hasAdvancedAnalytics: false,
      hasPurchaseHistory: false,
      hasXlsxExport: false,
      hasXlsxImport: false,
      hasLowStockAlerts: true,
    },
    features: [
      'Up to 25 products/recipes',
      'Up to 100 raw materials',
      'Up to 3 users',
      'Full inventory tracking + low-stock alerts',
      'Cost auto-recalculation',
      'Basic dashboard + light analytics',
    ],
  },
  precision: {
    id: 'precision',
    name: 'InventIQ Precision',
    tagline: 'For growing operations and multi-category producers',
    priceMin: 79,
    priceMax: 129,
    priceSuffix: '/month',
    paystackPlanCode: process.env.NEXT_PUBLIC_PAYSTACK_PLAN_PRECISION ?? '',
    limits: {
      products: 200,
      rawMaterials: 500,
      users: 10,
      hasReporting: true,
      hasAdvancedAnalytics: true,
      hasPurchaseHistory: true,
      hasXlsxExport: true,
      hasXlsxImport: true,
      hasLowStockAlerts: true,
    },
    features: [
      'Up to 200 products/recipes',
      'Up to 500 raw materials',
      'Up to 10 users',
      'Advanced analytics (profit margin, batch costing, waste tracking)',
      'Purchase history',
      'Exportable reports (XLSX)',
      'XLSX import functionality',
    ],
  },
  whitelisted: {
    id: 'whitelisted',
    name: 'Full Access',
    tagline: 'Complimentary full access',
    priceMin: 0,
    priceMax: 0,
    priceSuffix: 'forever',
    paystackPlanCode: '',
    limits: {
      products: -1,
      rawMaterials: -1,
      users: -1,
      hasReporting: true,
      hasAdvancedAnalytics: true,
      hasPurchaseHistory: true,
      hasXlsxExport: true,
      hasXlsxImport: true,
      hasLowStockAlerts: true,
    },
    features: ['Unlimited everything'],
  },
};

// Whitelisted emails — always get full access
export const WHITELISTED_EMAILS = ['audreygroenewald@yahoo.com', 'zwbookworm@yahoo.co.uk'];

export function getPlanLimits(planId: PlanId): PlanLimits {
  return PLANS[planId]?.limits ?? PLANS.launch_pass.limits;
}

export function isUnlimited(value: number): boolean {
  return value === -1;
}

export function formatLimit(value: number): string {
  return value === -1 ? 'Unlimited' : value.toString();
}

export function canAddMore(current: number, limit: number): boolean {
  if (limit === -1) return true;
  return current < limit;
}
